"use client";

import { useEffect, useState } from "react";
import {
  CarFront,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  X,
} from "lucide-react";
import { TbCamera } from "react-icons/tb";

function BentoBox({ title, icon: Icon, children, className }) {
  return (
    <div className={`rounded-lg bg-[#292826] p-4 shadow ${className}`}>
      <h3 className="mb-4 flex items-center text-lg font-semibold text-white">
        <Icon className="mr-2 h-5 w-5" />
        {title}
      </h3>
      {children}
    </div>
  );
}

const STORAGE_BASE_URL = "https://new.cajuu.app/storage";

function getPhotoUrl(path) {
  if (!path) return "/img/cajuu_logo.png";

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  return `${STORAGE_BASE_URL}/${String(path).replace(/^\/+/, "")}`;
}

function getVehicleLabel(item, index) {
  const parts = [item?.year, item?.make, item?.model].filter(Boolean);

  if (parts.length > 0) {
    return parts.join(" ");
  }

  return `Vehicle ${index + 1}`;
}

function getOperableLabel(item) {
  const value =
    item?.operable ??
    item?.is_operable ??
    item?.vehicle_condition ??
    item?.condition;

  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "boolean") {
    return value ? "OP" : "INOP";
  }

  if (typeof value === "number") {
    return value === 1 ? "OP" : "INOP";
  }

  const normalized = String(value).trim().toUpperCase();

  if (
    normalized === "1" ||
    normalized === "TRUE" ||
    normalized === "YES" ||
    normalized === "Y" ||
    normalized === "OPERABLE" ||
    normalized === "OP"
  ) {
    return "OP";
  }

  if (
    normalized === "0" ||
    normalized === "FALSE" ||
    normalized === "NO" ||
    normalized === "N" ||
    normalized === "INOPERABLE" ||
    normalized === "INOP"
  ) {
    return "INOP";
  }

  return normalized;
}

function normalizeVehicles(selectedLoad) {
  const items = selectedLoad?.vehicles || selectedLoad?.load_items || [];

  return items.map((item, index) => {
    const label = getVehicleLabel(item, index);
    const operableLabel = getOperableLabel(item);

    const photos = Array.isArray(item?.inspection_photos)
      ? item.inspection_photos.map((photo, photoIndex) => ({
          id: photo?.id ?? `${item?.id ?? index}-${photoIndex}`,
          path: photo?.path ?? photo?.url ?? photo?.photo_path ?? null,
          alt: `${label} - Photo ${photoIndex + 1}`,
        }))
      : [];

    return {
      id: item?.id ?? `vehicle-${index}`,
      label,
      operableLabel,
      item,
      photos,
    };
  });
}

export function VehiclePhotoCarousel({ selectedLoad }) {
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicleIndex, setSelectedVehicleIndex] = useState(0);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  useEffect(() => {
    const normalizedVehicles = normalizeVehicles(selectedLoad);
    setVehicles(normalizedVehicles);

    if (normalizedVehicles.length === 0) {
      setSelectedVehicleIndex(0);
      setCurrentPhotoIndex(0);
      setIsLoading(false);
      return;
    }

    const firstVehicleWithPhotos = normalizedVehicles.findIndex(
      (vehicle) => vehicle.photos.length > 0
    );

    const initialVehicleIndex =
      firstVehicleWithPhotos >= 0 ? firstVehicleWithPhotos : 0;

    setSelectedVehicleIndex(initialVehicleIndex);
    setCurrentPhotoIndex(0);
    setIsLoading(normalizedVehicles[initialVehicleIndex]?.photos.length > 0);
  }, [selectedLoad]);

  useEffect(() => {
    if (!isFullscreen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isFullscreen]);

  const selectedVehicle = vehicles[selectedVehicleIndex] || null;
  const currentPhotos = selectedVehicle?.photos || [];
  const currentPhoto = currentPhotos[currentPhotoIndex] || null;
  const hasAnyPhotos = vehicles.some((vehicle) => vehicle.photos.length > 0);

  const findNextVehicleWithPhotos = (startIndex, direction) => {
    if (vehicles.length === 0 || !hasAnyPhotos) return -1;

    for (let step = 1; step <= vehicles.length; step += 1) {
      const nextIndex =
        (startIndex + step * direction + vehicles.length) % vehicles.length;

      if ((vehicles[nextIndex]?.photos?.length || 0) > 0) {
        return nextIndex;
      }
    }

    return -1;
  };

  const selectVehicle = (vehicleIndex, photoIndex = 0) => {
    const targetVehicle = vehicles[vehicleIndex];
    if (!targetVehicle) return;

    setSelectedVehicleIndex(vehicleIndex);
    setCurrentPhotoIndex(photoIndex);
    setIsLoading(targetVehicle.photos.length > 0);
  };

  const handlePrevious = () => {
    if (!hasAnyPhotos) return;

    if (currentPhotos.length === 0) {
      const previousVehicleIndex = findNextVehicleWithPhotos(
        selectedVehicleIndex,
        -1
      );

      if (previousVehicleIndex >= 0) {
        const previousVehiclePhotos = vehicles[previousVehicleIndex].photos;
        selectVehicle(
          previousVehicleIndex,
          Math.max(previousVehiclePhotos.length - 1, 0)
        );
      }

      return;
    }

    if (currentPhotoIndex > 0) {
      setCurrentPhotoIndex((prev) => prev - 1);
      setIsLoading(true);
      return;
    }

    const previousVehicleIndex = findNextVehicleWithPhotos(
      selectedVehicleIndex,
      -1
    );

    if (previousVehicleIndex >= 0) {
      const previousVehiclePhotos = vehicles[previousVehicleIndex].photos;
      selectVehicle(
        previousVehicleIndex,
        Math.max(previousVehiclePhotos.length - 1, 0)
      );
    }
  };

  const handleNext = () => {
    if (!hasAnyPhotos) return;

    if (currentPhotos.length === 0) {
      const nextVehicleIndex = findNextVehicleWithPhotos(
        selectedVehicleIndex,
        1
      );

      if (nextVehicleIndex >= 0) {
        selectVehicle(nextVehicleIndex, 0);
      }

      return;
    }

    if (currentPhotoIndex < currentPhotos.length - 1) {
      setCurrentPhotoIndex((prev) => prev + 1);
      setIsLoading(true);
      return;
    }

    const nextVehicleIndex = findNextVehicleWithPhotos(selectedVehicleIndex, 1);

    if (nextVehicleIndex >= 0) {
      selectVehicle(nextVehicleIndex, 0);
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart === null || touchEnd === null) return;

    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;

    if (distance > minSwipeDistance) handleNext();
    if (distance < -minSwipeDistance) handlePrevious();

    setTouchStart(null);
    setTouchEnd(null);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isFullscreen) return;

      if (e.key === "ArrowLeft") handlePrevious();
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "Escape") setIsFullscreen(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFullscreen, selectedVehicleIndex, currentPhotoIndex, vehicles]);

  if (
    (selectedLoad?.vehicles || selectedLoad?.load_items) &&
    (selectedLoad?.vehicles || selectedLoad?.load_items).length === 0
  ) {
    return null;
  }

  const renderMainImage = (fullscreen = false) => {
    const imageWrapperHeight = fullscreen
      ? "h-[calc(100vh-250px)]"
      : "h-[260px] sm:h-[340px] lg:h-[520px]";

    if (!selectedVehicle) {
      return (
        <div className="flex h-40 items-center justify-center text-gray-400">
          No photos available
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <div
          className={`relative overflow-hidden rounded-2xl bg-black ${imageWrapperHeight}`}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {currentPhoto ? (
            <>
              <div className="flex h-full w-full items-center justify-center">
                <img
                  src={getPhotoUrl(currentPhoto.path)}
                  alt={currentPhoto.alt}
                  className={`h-full w-full object-contain transition-opacity duration-300 ${
                    isLoading ? "opacity-0" : "opacity-100"
                  }`}
                  onLoad={() => setIsLoading(false)}
                  onError={() => setIsLoading(false)}
                />
              </div>

              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-amber-500" />
                </div>
              )}

              {currentPhotos.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={handlePrevious}
                    className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white transition-all hover:bg-black/70"
                    aria-label="Previous photo"
                  >
                    <ChevronLeft size={24} />
                  </button>

                  <button
                    type="button"
                    onClick={handleNext}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white transition-all hover:bg-black/70"
                    aria-label="Next photo"
                  >
                    <ChevronRight size={24} />
                  </button>
                </>
              )}

              {!fullscreen && (
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  className="absolute right-2 top-2 rounded-full bg-black/50 p-2 text-white transition-all hover:bg-black/70"
                  aria-label="Toggle fullscreen"
                >
                  <Maximize2 size={18} />
                </button>
              )}
            </>
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-gray-400">
              <CarFront className="h-10 w-10 opacity-70" />
              <span>No photos available for this vehicle</span>
            </div>
          )}
        </div>

        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="truncate text-base font-semibold text-gray-200 sm:text-[1.15rem]">
              {selectedVehicle.label}
            </div>
            <div className="text-sm text-gray-400">
              {selectedVehicle.operableLabel && (
                <span
                  className={
                    selectedVehicle.operableLabel === "OP"
                      ? "text-green-400"
                      : selectedVehicle.operableLabel === "INOP"
                      ? "text-red-400"
                      : "text-gray-400"
                  }
                >
                  {selectedVehicle.operableLabel}
                </span>
              )}
              {selectedVehicle.operableLabel ? " • " : ""}
              {currentPhotos.length} photo{currentPhotos.length === 1 ? "" : "s"}
            </div>
          </div>

          <div className="shrink-0 text-sm text-gray-300">
            {currentPhotos.length > 0
              ? `${currentPhotoIndex + 1} / ${currentPhotos.length}`
              : "0 / 0"}
          </div>
        </div>
      </div>
    );
  };

  const renderVehicleSelector = () => {
    if (vehicles.length === 0) return null;

    return (
      <div className="xl:h-[520px] xl:self-start">
        <div className="flex gap-3 overflow-x-auto pb-1 xl:h-full xl:flex-col xl:overflow-x-hidden xl:overflow-y-auto xl:pr-1">
          {vehicles.map((vehicle, index) => {
            const isSelected = index === selectedVehicleIndex;
            const hasPhotos = vehicle.photos.length > 0;

            return (
              <button
                type="button"
                key={vehicle.id}
                onClick={() => {
                  if (!hasPhotos) return;
                  selectVehicle(index, 0);
                }}
                disabled={!hasPhotos}
                className={`min-w-[240px] rounded-2xl border p-4 text-left transition-all xl:min-w-0 ${
                  isSelected
                    ? "border-amber-500 bg-amber-500/20"
                    : "border-white/20 bg-black/20 hover:border-white/30 hover:bg-black/30"
                } ${
                  !hasPhotos
                    ? "cursor-not-allowed opacity-50"
                    : "cursor-pointer"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                      isSelected
                        ? "bg-amber-500/20 text-amber-300"
                        : "bg-white/5 text-gray-300"
                    }`}
                  >
                    <CarFront className="h-5 w-5" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-base font-semibold text-white">
                        {vehicle.label}
                      </span>

                      {vehicle.operableLabel && (
                        <span
                          className={`shrink-0 text-sm font-medium ${
                            vehicle.operableLabel === "OP"
                              ? "text-green-400"
                              : vehicle.operableLabel === "INOP"
                              ? "text-red-400"
                              : "text-gray-300"
                          }`}
                        >
                          {vehicle.operableLabel}
                        </span>
                      )}
                    </div>

                    <div className="mt-1 text-sm text-gray-400">
                      {vehicle.photos.length} photo
                      {vehicle.photos.length === 1 ? "" : "s"}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderThumbnails = () => {
    if (currentPhotos.length <= 1) return null;

    return (
      <div className="w-full">
        <div className="flex w-full gap-2 overflow-x-auto pb-2">
          {currentPhotos.map((photo, index) => (
            <button
              type="button"
              key={photo.id}
              onClick={() => {
                setCurrentPhotoIndex(index);
                setIsLoading(true);
              }}
              className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 transition-all sm:h-20 sm:w-20 ${
                currentPhotoIndex === index
                  ? "border-amber-500"
                  : "border-transparent"
              }`}
              aria-label={`Select photo ${index + 1}`}
            >
              <img
                src={getPhotoUrl(photo.path)}
                alt={photo.alt}
                className="h-full w-full object-cover"
              />
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <BentoBox
        title="Vehicle Photos"
        className="col-span-1 md:col-span-2"
        icon={TbCamera}
      >
        {vehicles.length > 0 ? (
          <div className="space-y-4">
            {/* Linha superior: imagem + coluna de veículos */}
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.75fr)_320px] xl:items-start">
              <div className="min-w-0">{renderMainImage(false)}</div>
              <div className="min-w-0">{renderVehicleSelector()}</div>
            </div>

            {/* Linha inferior: thumbs ocupando 100% da largura */}
            <div className="w-full">{renderThumbnails()}</div>
          </div>
        ) : (
          <div className="flex h-40 items-center justify-center text-gray-400">
            No photos available
          </div>
        )}
      </BentoBox>

      {isFullscreen && currentPhoto && (
        <div className="fixed inset-0 z-[9999] bg-black/95 p-3 sm:p-6">
          <button
            type="button"
            onClick={() => setIsFullscreen(false)}
            className="absolute right-3 top-3 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20"
            aria-label="Close fullscreen"
          >
            <X size={20} />
          </button>

          <div className="mx-auto flex h-full max-w-7xl flex-col gap-4 pt-10">
            <div className="w-full">{renderMainImage(true)}</div>
            <div className="w-full">{renderThumbnails()}</div>
          </div>
        </div>
      )}
    </>
  );
}